export const MAX_MENTIONS_INPUT_HEIGHT = 120;

const NO_FORMATS = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  unordered: false,
  ordered: false,
};

/**
 * Toolbar state for a rich composer. queryCommandState reads the document selection wherever
 * it is, so a selection outside the editor (e.g. a bold chat-list title) would light up "B";
 * only a selection inside `editor` counts, anything else is all off.
 */
export const readComposerFormats = (editor) => {
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  const node = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).startContainer : null;
  if (!editor || !node || !editor.contains(node)) return NO_FORMATS;
  return {
    bold: document.queryCommandState("bold"),
    italic: document.queryCommandState("italic"),
    underline: document.queryCommandState("underline"),
    strikethrough: document.queryCommandState("strikeThrough"),
    unordered: document.queryCommandState("insertUnorderedList"),
    ordered: document.queryCommandState("insertOrderedList"),
  };
};

export const applyActiveFormatsIfChanged = (setActiveFormats, formatsRef, next) => {
  const prev = formatsRef?.current;
  if (
    prev &&
    prev.bold === next.bold &&
    prev.italic === next.italic &&
    prev.underline === next.underline &&
    prev.strikethrough === next.strikethrough &&
    prev.unordered === next.unordered &&
    prev.ordered === next.ordered
  ) {
    return false;
  }
  if (formatsRef) formatsRef.current = next;
  setActiveFormats(next);
  return true;
};

// Keep React state in sync only when the composer goes empty ↔ non-empty.
// Per-keystroke setState re-renders the whole conversation (and decrypts every
// bubble), which is what makes typing feel delayed.
export const syncComposerEmptiness = (setMessage, rawText) => {
  const next = rawText != null ? String(rawText).replace(/\u00A0/g, " ") : "";
  const nextHad = !!next.trim();
  setMessage((prev) => {
    const prevHad = !!(prev && String(prev).trim());
    if (prevHad === nextHad) return prev;
    return nextHad ? next.trim() : "";
  });
};

export const readLiveComposerText = ({
  messageOverride,
  isToolbarOpen,
  getRichEditorContent,
  messageRef,
  fallback,
}) => {
  if (messageOverride != null && String(messageOverride).length) {
    return String(messageOverride);
  }
  if (isToolbarOpen && typeof getRichEditorContent === "function") {
    try {
      const fromEditor = getRichEditorContent();
      if (fromEditor && String(fromEditor).trim()) return String(fromEditor);
    } catch (e) {
      // Fall through
    }
  }
  if (messageRef?.current && String(messageRef.current).trim()) {
    return String(messageRef.current);
  }
  return fallback != null ? String(fallback) : "";
};

export const resizeTextarea = (textarea) => {
  if (!textarea) return;
  textarea.style.height = 'auto';
  const minHeight = 20;
  const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), MAX_MENTIONS_INPUT_HEIGHT);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = textarea.scrollHeight > MAX_MENTIONS_INPUT_HEIGHT ? 'auto' : 'hidden';
};

export const composerMarkdownToHtml = (md) => {
  if (!md) return "";
  let html = md;
  html = html.replace(/^((?:- .+(?:\n|$))+)/gm, (block) => {
    const items = block.trim().split('\n').map(line => `<li>${line.replace(/^- /, '')}</li>`).join('');
    return `<ul>${items}</ul>`;
  });
  html = html.replace(/^((?:\d+\. .+(?:\n|$))+)/gm, (block) => {
    const items = block.trim().split('\n').map(line => `<li>${line.replace(/^\d+\.\s*/, '')}</li>`).join('');
    return `<ol>${items}</ol>`;
  });
  html = html.replace(/\*\*([\s\S]*?)\*\*/g, '<b>$1</b>');
  html = html.replace(/~~([\s\S]*?)~~/g, '<del>$1</del>');
  html = html.replace(/@\[([^\]]*)\]\(([^)]*)\)/g, '<span class="mention-tag" data-mention-id="$2" data-mention-name="$1" contenteditable="false">@$1</span>');
  html = html.replace(/\[([^\]]*)\]\(([^)]*)\)/g, '<a href="$2">$1</a>');
  html = html.replace(/\n\n+/g, "</div><div>");
  html = html.replace(/\n/g, "<br>");
  if (html.includes("</div><div>")) {
    html = `<div>${html}</div>`;
  }
  return html;
};

const isVisuallyEmptyBlock = (inner) =>
  !String(inner || "")
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, "")
    .replace(/\u00A0/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\s/g, "");

export const htmlToMarkdown = (html) => {
  if (!html) return "";
  let md = html;
  md = md.replace(/<li>([\s\S]*?)<\/li>/gi, (_, content) => {
    let clean = content.replace(/<br\s*\/?>/gi, "").trim();
    clean = clean.replace(/<b>([\s\S]*?)<\/b>/gi, "**$1**");
    clean = clean.replace(/<strong>([\s\S]*?)<\/strong>/gi, "**$1**");
    clean = clean.replace(/<i>([\s\S]*?)<\/i>/gi, "<em>$1</em>");
    clean = clean.replace(/<em>([\s\S]*?)<\/em>/gi, "<em>$1</em>");
    clean = clean.replace(/<u>([\s\S]*?)<\/u>/gi, "<u>$1</u>");
    clean = clean.replace(/<s>([\s\S]*?)<\/s>/gi, "~~$1~~");
    clean = clean.replace(/<strike>([\s\S]*?)<\/strike>/gi, "~~$1~~");
    clean = clean.replace(/<del>([\s\S]*?)<\/del>/gi, "~~$1~~");
    clean = clean.replace(/<span[^>]*class="mention-tag"[^>]*>[\s\S]*?<\/span>/gi, (m) => {
      const idM = m.match(/data-mention-id="([^"]*)"/);
      const nameM = m.match(/data-mention-name="([^"]*)"/);
      return (idM && nameM) ? `@[${nameM[1]}](${idM[1]})` : m;
    });
    clean = clean.replace(/<(?!\/?(?:u|em)>)[^>]+>/g, "");
    return `<li>${clean}</li>`;
  });
  md = md.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner) => {
    const items = [];
    inner.replace(/<li>([\s\S]*?)<\/li>/gi, (__, content) => { items.push("- " + content.trim()); });
    return "\n\n" + items.join("\n") + "\n\n";
  });
  md = md.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) => {
    const items = [];
    let i = 0;
    inner.replace(/<li>([\s\S]*?)<\/li>/gi, (__, content) => { i++; items.push(i + ". " + content.trim()); });
    return "\n\n" + items.join("\n") + "\n\n";
  });
  md = md.replace(/<b>([\s\S]*?)<\/b>/gi, "**$1**");
  md = md.replace(/<strong>([\s\S]*?)<\/strong>/gi, "**$1**");
  md = md.replace(/<i>([\s\S]*?)<\/i>/gi, "<em>$1</em>");
  md = md.replace(/<em>([\s\S]*?)<\/em>/gi, "<em>$1</em>");
  md = md.replace(/<u>([\s\S]*?)<\/u>/gi, "<u>$1</u>");
  md = md.replace(/<s>([\s\S]*?)<\/s>/gi, "~~$1~~");
  md = md.replace(/<strike>([\s\S]*?)<\/strike>/gi, "~~$1~~");
  md = md.replace(/<del>([\s\S]*?)<\/del>/gi, "~~$1~~");
  md = md.replace(/<span[^>]*class="mention-tag"[^>]*>[\s\S]*?<\/span>/gi, (m) => {
    const idM = m.match(/data-mention-id="([^"]*)"/);
    const nameM = m.match(/data-mention-name="([^"]*)"/);
    return (idM && nameM) ? `@[${nameM[1]}](${idM[1]})` : m;
  });
  md = md.replace(/<a\s+href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, "[$2]($1)");
  // contentEditable often keeps the first line unwrapped: "Hello<div>next</div>".
  // Without a leading newline that becomes "Hellonext" after tags are stripped.
  md = md.replace(/<div[^>]*>([\s\S]*?)<\/div>/gi, (_, inner) =>
    isVisuallyEmptyBlock(inner) ? "\n\n" : `\n\n${inner}`
  );
  md = md.replace(/<br\s*\/?>/gi, "\n");
  // Paragraphs must stay paragraphs (`\n\n`). A single `\n` becomes a <br>
  // after send, which collapses the empty-line gap the composer shows.
  md = md.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (_, inner) =>
    isVisuallyEmptyBlock(inner) ? "\n\n" : `\n\n${inner}`
  );
  md = md.replace(/<(?!\/?(?:u|em)>)[^>]+>/g, "");
  md = md.replace(/&nbsp;/g, " ");
  md = md.replace(/&amp;/g, "&");
  md = md.replace(/&lt;/g, "<");
  md = md.replace(/&gt;/g, ">");
  md = md.replace(/\*\*([\s\S]*?)\*\*/g, (_, content) => {
    const m = content.match(/^(\s*)([\s\S]*?)(\s*)$/);
    if (!m || !m[2]) return content;
    return `${m[1]}**${m[2]}**${m[3]}`;
  });
  md = md.replace(/~~([\s\S]*?)~~/g, (_, content) => {
    const m = content.match(/^(\s*)([\s\S]*?)(\s*)$/);
    if (!m || !m[2]) return content;
    return `${m[1]}~~${m[2]}~~${m[3]}`;
  });
  md = md.replace(/\n{3,}/g, "\n\n");
  return md.trim();
};

export const cleanPastedHtml = (html) => {
  let clean = html;
  const fragMatch = clean.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i);
  if (fragMatch) clean = fragMatch[1];
  clean = clean.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, "$1<br>");
  clean = clean.replace(/(<br\s*\/?\s*>)+$/i, "");
  clean = clean.replace(/\n/g, "");
  return clean.trim();
};

const clipboardHasImageOrFile = (data) => {
  if (!data) return false;
  try {
    const files = data.files;
    if (files && files.length > 0) return true;
    const items = data.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") return true;
        if (item.type && item.type.indexOf("image") !== -1) return true;
      }
    }
  } catch (e) {
    return false;
  }
  return false;
};

export const collectPastedImageFiles = (clipboardData) => {
  const collected = [];
  const seen = new Set();
  const addFile = (file) => {
    if (!file || typeof file.type !== "string") return;
    const isImage =
      file.type.indexOf("image") !== -1 ||
      /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || "");
    if (!isImage) return;
    // Screenshot pastes expose the same image via items AND files, often as
    // two File objects with different lastModified — dedupe on name/size/type.
    const key = `${file.name}|${file.size}|${file.type}`;
    if (seen.has(key)) return;
    seen.add(key);
    collected.push(
      Object.assign(file, {
        url: URL.createObjectURL(file),
      })
    );
  };
  try {
    const items = clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" || (item.type && item.type.indexOf("image") !== -1)) {
          addFile(item.getAsFile());
        }
      }
    }
    // files is the same list as kind=file items; only use it as a fallback
    // when items did not yield an image (some browsers omit image MIME on items).
    if (collected.length === 0) {
      const files = clipboardData?.files;
      if (files) {
        for (let i = 0; i < files.length; i++) addFile(files[i]);
      }
    }
  } catch (e) {
    return collected;
  }
  return collected;
};

const insertUnformattedText = (text, editor) => {
  if (!text) return;
  const ok = document.execCommand("insertText", false, text);
  if (ok) return;
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  editor?.dispatchEvent(new Event("input", { bubbles: true }));
};

/** Paste/drop clipboard contents as unformatted plain text (no bold, colors, lists, etc.). */
export const pasteAsPlainText = (event) => {
  try {
    const data = event?.clipboardData || event?.dataTransfer;
    if (!data) return false;
    // Screenshots include image files (and often huge HTML data-URIs). Never
    // intercept those — parsing that HTML whites out the tab.
    if (clipboardHasImageOrFile(data)) return false;

    const text = data.getData("text/plain") || "";
    if (!text) return false;

    event.preventDefault();
    insertUnformattedText(text.replace(/\r\n/g, "\n"), event.currentTarget);
    return true;
  } catch (e) {
    return false;
  }
};

export const fileFingerprint = (file) =>
  `${file?.clientId || file?.fileUrl || file?.name || file?.attachedFileName || ""}|${file?.size || 0}|${file?.lastModified || 0}|${file?.type || file?.fileType || ""}`;

/**
 * Union of dataTransfer.items and dataTransfer.files, de-duplicated.
 * Multi-file drops (especially from Explorer) often put a partial list in one
 * collection and the rest in the other; using only `files` drops images.
 */
export const getDroppedFiles = (dataTransfer) => {
  if (!dataTransfer) return [];
  const seen = new Set();
  const out = [];
  const push = (file) => {
    if (!file) return;
    const key = fileFingerprint(file);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(file);
  };
  if (dataTransfer.items && dataTransfer.items.length > 0) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i];
      if (item && item.kind === "file") push(item.getAsFile());
    }
  }
  if (dataTransfer.files && dataTransfer.files.length > 0) {
    for (let i = 0; i < dataTransfer.files.length; i++) {
      push(dataTransfer.files[i]);
    }
  }
  return out;
};

const dataTransferHasFiles = (data) => {
  if (!data) return false;
  if (data.files && data.files.length > 0) return true;
  if (data.items && data.items.length > 0) {
    for (let i = 0; i < data.items.length; i++) {
      if (data.items[i]?.kind === "file") return true;
    }
  }
  return false;
};

/** Keep file drops out of the editor so the chat Dropzone / preview can handle them. */
export const handleComposerDrop = (event) => {
  try {
    const data = event?.dataTransfer;
    if (!data) return;
    if (dataTransferHasFiles(data)) {
      event.preventDefault();
      return;
    }
    pasteAsPlainText(event);
  } catch (e) {
    /* never crash the conversation on drop */
  }
};

const MARKDOWN_LIST_LINE_RE = /^([ \t]*)([-*+•]|(\d+)[.)])([ \t]+)(.*)$/;
const LINE_BLOCK_RE = /^(DIV|P|LI|H[1-6]|BLOCKQUOTE|PRE)$/;

const parseMarkdownListLine = (line) => {
  const match = String(line || "").replace(/\u00A0/g, " ").match(MARKDOWN_LIST_LINE_RE);
  if (!match) return null;
  const indent = match[1];
  const marker = match[2];
  const number = match[3];
  const gap = match[4];
  const content = match[5];
  if (!String(content).trim()) {
    return { action: "exit", indent, deleteCount: line.length - indent.length };
  }
  const nextMarker =
    number != null ? `${Number(number) + 1}${marker.slice(String(number).length)}` : marker;
  return {
    action: "continue",
    nextPrefix: `${indent}${nextMarker}${gap}`,
  };
};

export const applyMarkdownListEnter = (value, cursorPos) => {
  const text = value != null ? String(value) : "";
  const pos = Math.max(0, Math.min(cursorPos ?? text.length, text.length));
  const lineStart = text.lastIndexOf("\n", pos - 1) + 1;
  const parsed = parseMarkdownListLine(text.slice(lineStart, pos));
  if (!parsed) return null;
  if (parsed.action === "exit") {
    const nextValue = `${text.slice(0, lineStart)}${parsed.indent}${text.slice(pos)}`;
    return { nextValue, nextCursor: lineStart + parsed.indent.length };
  }
  const insert = `\n${parsed.nextPrefix}`;
  return {
    nextValue: `${text.slice(0, pos)}${insert}${text.slice(pos)}`,
    nextCursor: pos + insert.length,
  };
};

const closestListAncestor = (node, editor) => {
  let current = node;
  while (current && current !== editor) {
    if (current.nodeName === "LI" || current.nodeName === "UL" || current.nodeName === "OL") {
      return current;
    }
    current = current.parentNode;
  }
  return null;
};

const getLineTextBeforeCursor = (editor) => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !editor) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;
  let block = range.startContainer;
  if (block.nodeType === Node.TEXT_NODE) block = block.parentNode;
  while (block && block !== editor && !LINE_BLOCK_RE.test(block.nodeName)) {
    block = block.parentNode;
  }
  if (!block) return null;
  const pre = document.createRange();
  try {
    pre.selectNodeContents(block);
    pre.setEnd(range.startContainer, range.startOffset);
  } catch (e) {
    return null;
  }
  const text = pre.toString().replace(/\u00A0/g, " ");
  return text.split("\n").pop();
};

const tryContinueMarkdownListInEditor = (editor) => {
  const line = getLineTextBeforeCursor(editor);
  if (line == null) return false;
  const parsed = parseMarkdownListLine(line);
  if (!parsed) return false;
  if (parsed.action === "continue") {
    document.execCommand("insertLineBreak");
    const ok = document.execCommand("insertText", false, parsed.nextPrefix);
    if (!ok) insertUnformattedText(parsed.nextPrefix, editor);
    return true;
  }
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    const { startContainer, startOffset } = range;
    if (
      startContainer.nodeType === Node.TEXT_NODE &&
      startOffset >= parsed.deleteCount
    ) {
      range.setStart(startContainer, startOffset - parsed.deleteCount);
      range.deleteContents();
      sel.removeAllRanges();
      sel.addRange(range);
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    }
  }
  const ok = document.execCommand("insertText", false, "\n");
  if (!ok) insertUnformattedText("\n", editor);
  return true;
};

const isComposerEnterComposing = (e) =>
  !!(e.nativeEvent?.isComposing || e.isComposing || e.keyCode === 229);

/**
 * Enter sends, Shift+Enter inserts a newline, and markdown / HTML lists
 * continue on Enter instead of sending.
 * @returns {'send' | 'handled' | 'ignored'}
 */
export const handleComposerEnterKey = (e, opts = {}) => {
  if (e.key !== "Enter") return "ignored";
  if (isComposerEnterComposing(e)) return "ignored";

  const { editor, textValue, selectionStart, onListReplace } = opts;

  if (e.shiftKey) {
    if (editor) {
      e.preventDefault();
      document.execCommand("insertLineBreak");
    }
    return "handled";
  }

  if (editor) {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
      if (closestListAncestor(sel.anchorNode, editor)) return "handled";
    }
    if (tryContinueMarkdownListInEditor(editor)) {
      e.preventDefault();
      return "handled";
    }
    e.preventDefault();
    return e.repeat ? "handled" : "send";
  }

  if (typeof textValue === "string" && typeof onListReplace === "function") {
    const pos =
      typeof selectionStart === "number" ? selectionStart : textValue.length;
    const result = applyMarkdownListEnter(textValue, pos);
    if (result) {
      e.preventDefault();
      onListReplace(result.nextValue, result.nextCursor);
      return "handled";
    }
  }

  e.preventDefault();
  return e.repeat ? "handled" : "send";
};

export const LOCALE_MAP = {
  'de': 'de',
  'en': 'en-gb',
  'fr': 'fr',
  'it': 'it'
};

export const getLocaleForLanguage = (language) => {
  return LOCALE_MAP[language?.toLowerCase()] || 'en-gb';
};
